import type { Parser, SyntaxNode } from '@lezer/common'
import { highlightTree, tagHighlighter, tags } from '@lezer/highlight'
import { parser as javascriptParser } from '@lezer/javascript'
import { parser, Table } from '@lezer/markdown'
import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { theme } from '@schmancy/theme/theme.service'
import { css, html, nothing, type PropertyValues, type TemplateResult } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import {
	catchError,
	combineLatest,
	concatMap,
	defer,
	distinctUntilChanged,
	from,
	map,
	of,
	shareReplay,
	startWith,
	Subject,
	switchMap,
	takeUntil,
} from 'rxjs'
import '../button/button'
import '../copy/copy'
import '../divider/divider'
import '../layout/scroll/scroll'
import '../link/link'
import '../typography/typography'

/** A piece of a line: a run of text, or one rendered mark. */
type Inline = string | TemplateResult

const documentParser = parser.configure(Table)
const typescriptParser = javascriptParser.configure({ dialect: 'ts' })
const jsonParser = javascriptParser.configure({ top: 'SingleExpression' })
const codeParsers: Record<string, Parser> = {
	json: jsonParser,
	js: javascriptParser,
	javascript: javascriptParser,
	jsx: javascriptParser.configure({ dialect: 'jsx' }),
	ts: typescriptParser,
	typescript: typescriptParser,
	tsx: javascriptParser.configure({ dialect: 'ts jsx' }),
}
const codeHighlighter = tagHighlighter([
	{ tag: tags.comment, class: 'text-surface-on-variant' },
	{ tag: tags.keyword, class: 'text-primary-default' },
	{ tag: [tags.string, tags.regexp], class: 'text-tertiary-default' },
	{ tag: [tags.number, tags.bool, tags.null, tags.typeName, tags.className], class: 'text-secondary-default' },
])
const jsonHighlighter = tagHighlighter([
	{ tag: tags.propertyName, class: 'text-primary-default' },
	{ tag: [tags.string, tags.escape], class: 'text-tertiary-default' },
	{ tag: [tags.number, tags.bool, tags.null], class: 'text-secondary-default' },
])

/** Format syntax tokens rather than parsed values, retaining duplicate keys and exact numeric/string lexemes. */
function formatJSON(source: string): string | null {
	try {
		JSON.parse(source)
	} catch {
		return null
	}
	const tokens: string[] = []
	jsonParser.parse(source).iterate({
		enter(node) {
			if (node.name === 'String' || node.node.firstChild === null) {
				tokens.push(source.slice(node.from, node.to))
				return false
			}
		},
	})
	let formatted = ''
	let depth = 0
	for (let index = 0; index < tokens.length; index++) {
		const token = tokens[index]
		if (token === '{' || token === '[') {
			formatted += token
			if (tokens[index + 1] !== (token === '{' ? '}' : ']')) {
				depth++
				formatted += `\n${'  '.repeat(depth)}`
			}
		} else if (token === '}' || token === ']') {
			if (tokens[index - 1] !== (token === '}' ? '{' : '[')) {
				depth--
				formatted += `\n${'  '.repeat(depth)}`
			}
			formatted += token
		} else if (token === ',') formatted += `,\n${'  '.repeat(depth)}`
		else if (token === ':') formatted += ': '
		else formatted += token
	}
	return formatted
}

const diagramRenderer$ = defer(() => import('mermaid')).pipe(
	map(({ default: mermaid }) => {
		mermaid.initialize({
			startOnLoad: false,
			securityLevel: 'strict',
			htmlLabels: false,
			maxTextSize: 20_000,
			maxEdges: 200,
			suppressErrorRendering: true,
			secure: [
				'secure',
				'securityLevel',
				'startOnLoad',
				'htmlLabels',
				'maxTextSize',
				'maxEdges',
				'suppressErrorRendering',
				'themeCSS',
			],
		})
		return mermaid
	}),
	shareReplay({ bufferSize: 1, refCount: false }),
)

/**
 * The syntax a mark is written WITH, which the rendered mark replaces. Every
 * one of these is dropped where it sits and its own characters never reach the
 * reading face — that literal `## ` is the whole complaint this component
 * answers.
 */
const MARKS = new Set([
	'HeaderMark',
	'QuoteMark',
	'ListMark',
	'CodeMark',
	'EmphasisMark',
	'LinkMark',
	'CodeInfo',
	'LinkTitle',
	'LinkLabel',
])

/**
 * Inside a link's own words the target is not one of them. It is NOT in
 * {@link MARKS}, because a bare autolink — `<https://example.com>` — is a `URL`
 * standing on its own, and a reader that skipped every URL would drop it.
 */
const LINK_PARTS = new Set([...MARKS, 'URL'])

/**
 * The heading scale, deepest heading nearest the body face it sits in — and the
 * LEVEL, because a heading has to be one. Rendering the scale alone produced a
 * document with zero heading roles in it: no outline, and no way for a screen
 * reader to move by heading (WCAG 1.3.1).
 */
const HEADINGS: Record<
	string,
	{
		readonly level: 1 | 2 | 3 | 4 | 5 | 6
		readonly type: 'title' | 'subtitle' | 'label'
		readonly token: 'sm' | 'md' | 'lg'
	}
> = {
	ATXHeading1: { level: 1, type: 'title', token: 'lg' },
	ATXHeading2: { level: 2, type: 'title', token: 'md' },
	ATXHeading3: { level: 3, type: 'title', token: 'sm' },
	ATXHeading4: { level: 4, type: 'subtitle', token: 'md' },
	ATXHeading5: { level: 5, type: 'subtitle', token: 'sm' },
	ATXHeading6: { level: 6, type: 'label', token: 'lg' },
	SetextHeading1: { level: 1, type: 'title', token: 'lg' },
	SetextHeading2: { level: 2, type: 'title', token: 'md' },
}

/**
 * The band a formatter's hard wrap lands in, and the marks that say a run of
 * lines is DATA rather than a wrapped sentence.
 *
 * A document's soft line breaks are kept by default, because a prompt that
 * interpolates a goods table verbatim has newlines that are content. But an
 * author who wrapped their own prose at 100 columns wrote lines whose breaks
 * are an artifact of their editor, and a reading face capped at the readable
 * measure — ~65ch — re-wraps those lines anyway, so keeping them produces a
 * two-word orphan mid-paragraph and makes the two wrap sources
 * indistinguishable. Measured on this corpus: 19 of 87 lines of one prompt and
 * 23 of 96 of another sit in this band.
 *
 * So the decision is per BLOCK and read off the source, not a boolean a caller
 * guesses once for a whole document.
 */
const WRAP_MIN = 80
const WRAP_MAX = 105

/** The six heading elements, so a level is a real element rather than an attribute. */
const HEADING_TAGS: Record<1 | 2 | 3 | 4 | 5 | 6, (inner: TemplateResult) => TemplateResult> = {
	1: inner => html`<h1 class="m-0 max-w-prose">${inner}</h1>`,
	2: inner => html`<h2 class="m-0 max-w-prose">${inner}</h2>`,
	3: inner => html`<h3 class="m-0 max-w-prose">${inner}</h3>`,
	4: inner => html`<h4 class="m-0 max-w-prose">${inner}</h4>`,
	5: inner => html`<h5 class="m-0 max-w-prose">${inner}</h5>`,
	6: inner => html`<h6 class="m-0 max-w-prose">${inner}</h6>`,
}

/** Whether this paragraph's newlines are a formatter's wrap rather than its content. */
function isHardWrapped(source: string): boolean {
	const lines = source.split('\n')
	if (lines.length < 3) return false
	// A pipe, a segment rule or a leading indent is the shape of interpolated
	// data, whose every newline was shown to the model exactly as it stands.
	if (lines.some(line => line.includes('|') || line.includes('===') || /^\s/.test(line))) return false
	// Every line but the LAST, which is the remainder and always short.
	return lines.slice(0, -1).every(line => line.length >= WRAP_MIN && line.length <= WRAP_MAX)
}

/**
 * The schemes a link in this document may navigate to. A document rendered here
 * is frequently harvested third-party text, so its `[…](…)` targets are not
 * this library's to hand a browser unfiltered: `javascript:` and `data:` are
 * both spelled as ordinary markdown links. A target outside this set renders as
 * the link's own text with no navigation, which loses nothing a reader can act
 * on — `Source` still holds the exact bytes.
 */
const NAVIGABLE = /^(https?:|mailto:|tel:|#|\/|\.{1,2}\/)/i

/** The five XML predefined entities plus the one a document actually writes. */
const NAMED_ENTITIES: Record<string, string> = {
	amp: '&',
	lt: '<',
	gt: '>',
	quot: '"',
	apos: "'",
	nbsp: ' ',
}

/** `&amp;` is the ampersand its author wrote, and reaches the page as text. */
function decodeEntity(source: string): string {
	const named = /^&([a-z]+);$/i.exec(source)
	if (named && named[1].toLowerCase() in NAMED_ENTITIES) return NAMED_ENTITIES[named[1].toLowerCase()]
	const numeric = /^&#(x?)([0-9a-f]+);$/i.exec(source)
	if (numeric) {
		const code = Number.parseInt(numeric[2], numeric[1] ? 16 : 10)
		if (Number.isFinite(code) && code > 0 && code <= 0x10ffff) return String.fromCodePoint(code)
	}
	return source
}

/** Every child of `node`, in source order. */
function childrenOf(node: SyntaxNode): readonly SyntaxNode[] {
	const children: SyntaxNode[] = []
	for (let child = node.firstChild; child !== null; child = child.nextSibling) children.push(child)
	return children
}

/** A heading's own words, with the space after its `##` gone. */
function trimEnds(parts: readonly Inline[]): readonly Inline[] {
	const out = [...parts]
	if (typeof out[0] === 'string') out[0] = out[0].replace(/^\s+/, '')
	const last = out.length - 1
	if (last >= 0 && typeof out[last] === 'string') out[last] = (out[last] as string).replace(/\s+$/, '')
	return out.filter(part => part !== '')
}

/**
 * A document in the product's reading face, with its marks rendered and its
 * line breaks left exactly as sent.
 *
 * ```html
 * <schmancy-markdown label="Prompt" subject="the rendered prompt of classify" .text=${prompt}></schmancy-markdown>
 * ```
 *
 * FOUR THOUSAND CHARACTERS OF INSTRUCTION IN A MONOSPACE `<pre>` IS NOT A
 * LISTING — it is prose set in the wrong face at the wrong measure. So the body
 * takes `<schmancy-typography type="body" token="md">` and caps at
 * `max-w-prose`, the readable-measure token schmancy already declares and
 * force-emits for exactly this job. A backtick span becomes a code span in the
 * monospace face and the primary tone with NO chip, because a box around each
 * of seventeen spans is a mark reporting what the face already reports.
 *
 * THE SOFT LINE BREAKS ARE KEPT, WHICH IS THE WHOLE DECISION. CommonMark joins
 * the lines of a paragraph, and doing that here destroys real data: a prompt
 * that interpolates a document's own `Pos. | Bezeichnung | Menge | Einzel € |
 * Gesamt €` goods table verbatim becomes one run-on line, and every newline in
 * it was content the model was shown. `breaks` therefore defaults TRUE; a
 * genuine CommonMark README, whose author wrapped their source and expects the
 * lines joined, sets it false.
 *
 * `Source` shows the exact bytes and is NOT a disclosure — both faces show the
 * whole document, so a rendering that ever mangles an appendix still leaves the
 * bytes reachable without leaving the page.
 *
 * NOTHING IS EVER CONCATENATED INTO HTML. `@lezer/markdown` is a parser, not a
 * renderer: it returns a tree with source positions, and this walks that tree
 * emitting Lit templates whose values bind as TEXT. So raw HTML inside a
 * document renders as the characters it is, an image is NAMED rather than
 * fetched, and a link outside {@link NAVIGABLE} carries no target. Mermaid
 * fences use a strict renderer and an SVG image, never SVG injected into the
 * application DOM.
 */
@schmancyElement('schmancy-markdown')
export class SchmancyMarkdown extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				min-width: 0;
			}
			[data-sticky-header] {
				position: sticky;
				top: 0;
				z-index: 10;
				padding-block: 0.25rem;
				background: var(--schmancy-markdown-header-ground, var(--schmancy-sys-color-surface-container));
			}
			[data-document] {
				grid-template-columns: minmax(0, 1fr);
				overflow-wrap: anywhere;
			}
		`,
	]

	/** The document, in markdown. */
	@property({ type: String }) text = ''

	/** Explicit JSON includes scalar values; Markdown also recognizes complete JSON objects and arrays. */
	@property({ type: String }) format: 'markdown' | 'json' = 'markdown'

	/** Embedded reading omits document controls while retaining the same semantic body. */
	@property({ type: String }) presentation: 'document' | 'embedded' = 'document'

	/**
	 * The document's header stays in view while a long document scrolls under it, with its copy and Source in reach.
	 * It paints `--schmancy-markdown-header-ground`, which is the tone it sits on: `surface-container` unless set.
	 */
	@property({ type: Boolean, attribute: 'sticky-header' }) stickyHeader = false

	/** Resolves navigation targets before scheme filtering; null leaves the link's words readable. */
	@property({ attribute: false }) resolveHref?: (href: string) => string | null

	/** Scrolls to the heading with this exact rendered title. */
	@property({ attribute: false }) heading = ''

	/** The header eyebrow: what this document IS — `Prompt`, `Notes`. */
	@property({ type: String }) label = ''

	/**
	 * What this document is OF — "the rendered prompt of classify". It is the
	 * reader's accessible name, the subject inside every control's name, and the
	 * subject of the copy control.
	 */
	@property({ type: String }) subject = ''

	/** Stands in the document's place when there is none, and says WHY there is none. */
	@property({ type: String }) absent = ''

	/** Reading the document, or the bytes behind it. */
	@state() private source = false
	@state() private diagrams = new Map<
		number,
		{ image?: string; alt?: string; width?: number; height?: number; error?: unknown }
	>()
	private documents$ = new Subject<string>()

	connectedCallback() {
		super.connectedCallback()
		combineLatest([this.documents$.pipe(startWith(this.text), distinctUntilChanged()), theme.theme$])
			.pipe(
				switchMap(([text]) => {
					this.diagrams = new Map()
					const blocks: { from: number; code: string }[] = []
					const cursor = documentParser.parse(text).cursor()
					do {
						if (cursor.name !== 'FencedCode') continue
						const info = cursor.node.getChild('CodeInfo')
						if (info === null || text.slice(info.from, info.to).trim() !== 'mermaid') continue
						blocks.push({
							from: cursor.from,
							code: cursor.node
								.getChildren('CodeText')
								.map(node => text.slice(node.from, node.to))
								.join('\n'),
						})
					} while (cursor.next())
					return from(blocks).pipe(
						concatMap(block =>
							diagramRenderer$.pipe(
								switchMap(async mermaid => {
									// Diagram-owned configuration and resources cannot fetch while Mermaid measures its SVG.
									if (
										block.code.length > 20_000 ||
										/^\s*---/.test(block.code) ||
										/%%\s*\{|(?:https?:|data:|file:|blob:|url\s*\(|\bimg\s*:|\bimage\s*:)/i.test(block.code)
									) {
										throw new Error('Diagram configuration, external resources or size are outside the reader limits.')
									}
									const styles = getComputedStyle(this)
									const palette = {
										primaryColor: styles.getPropertyValue('--schmancy-sys-color-surface-container').trim(),
										primaryTextColor: styles.getPropertyValue('--schmancy-sys-color-surface-on').trim(),
										primaryBorderColor: styles.getPropertyValue('--schmancy-sys-color-outline').trim(),
										lineColor: styles.getPropertyValue('--schmancy-sys-color-surface-on').trim(),
										textColor: styles.getPropertyValue('--schmancy-sys-color-surface-on').trim(),
										edgeLabelBackground: styles.getPropertyValue('--schmancy-sys-color-surface-default').trim(),
									}
									const { svg } = await mermaid.render(
										`schmancy-diagram-${crypto.randomUUID()}`,
										`---\nconfig: ${JSON.stringify({ theme: 'base', themeVariables: Object.fromEntries(Object.entries(palette).filter(([, value]) => value !== '')) })}\n---\n${block.code}`,
									)
									const document = new DOMParser().parseFromString(svg, 'image/svg+xml')
									const box = document.documentElement
										.getAttribute('viewBox')
										?.trim()
										.split(/[\s,]+/)
										.map(Number)
									if (
										!box ||
										box.length !== 4 ||
										!box.every(Number.isFinite) ||
										box[2] <= 0 ||
										box[3] <= 0 ||
										box[2] > 10_000 ||
										box[3] > 10_000
									) {
										throw new Error('The diagram dimensions are outside the reader limits.')
									}
									return {
										from: block.from,
										image: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
										width: Math.ceil(box[2]),
										height: Math.ceil(box[3]),
										alt:
											[document.querySelector('title')?.textContent, document.querySelector('desc')?.textContent]
												.filter(Boolean)
												.join('. ') ||
											this.subject ||
											this.label,
									}
								}),
								catchError(error => of({ from: block.from, error })),
							),
						),
					)
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe(result => {
				this.diagrams = new Map(this.diagrams).set(result.from, result)
			})
	}

	protected willUpdate(changes: PropertyValues) {
		if (changes.has('text')) this.documents$.next(this.text)
	}

	protected updated(changes: PropertyValues) {
		if (
			this.heading &&
			(changes.has('heading') || changes.has('text') || changes.has('source') || changes.has('diagrams'))
		) {
			this.scrollToHeading(this.heading)
		}
	}

	/** Scrolls to an exact rendered heading, including repeated requests for the same title. */
	public scrollToHeading(title: string): void {
		Array.from(this.renderRoot.querySelectorAll<HTMLElement>('h1,h2,h3,h4,h5,h6'))
			.find(node => node.textContent?.trim() === title)
			?.scrollIntoView({ block: 'start' })
	}

	protected firstUpdated() {
		if (import.meta.env.DEV && !this.subject) {
			console.warn(
				'[schmancy] <schmancy-markdown> has no `subject`, so every control inside it answers to a bare verb — indistinguishable from the reader next to it, and its copy control announces nothing. Give it the noun it reads: subject="the rendered prompt of classify".',
				this,
			)
		}
	}

	/** Counts read as prose, in the reader's own locale. */
	private count(n: number): string {
		return new Intl.NumberFormat(this.locale).format(n)
	}

	protected render() {
		const empty = this.text === ''
		const first = this.text.trimStart()[0]
		const json = this.format === 'json' || first === '{' || first === '[' ? formatJSON(this.text) : null
		const isJSON = this.format === 'json' || json !== null
		// WORDS, not source lines. A line count is a count of serialiser output —
		// the very artifact the reading face exists to hide — and it disagreed with
		// what was on screen the moment anything wrapped: `Prompt 2 lines` sat above
		// four visual lines.
		const words = empty ? 0 : this.text.split(/\s+/).filter(Boolean).length

		return html`
			<section class="grid gap-3 min-w-0" role="group" aria-label=${this.subject || this.label}>
				${when(
					this.presentation === 'document',
					() => html`
						<div class="flex items-center gap-3 flex-wrap min-h-6" ?data-sticky-header=${this.stickyHeader}>
							<schmancy-typography type="label" token="lg" class="text-surface-on-variant"
								>${this.label}</schmancy-typography
							>
							<schmancy-typography type="label" token="md" class="text-surface-on-variant tabular-nums"
								>${when(!empty, () =>
									isJSON
										? componentText('markdown.json')
										: componentText(words === 1 ? 'reader.measure.words.one' : 'reader.measure.words.other', {
												count: this.count(words),
											}),
								)}</schmancy-typography
							>
							${when(
								!empty,
								() => html`
									<!-- One word in both faces; the face is aria-pressed. -->
									<schmancy-button
										size="xs"
										variant="text"
										color="neutral"
										class="p-0"
										aria-pressed=${this.source ? 'true' : 'false'}
										aria-label=${componentText('reader.view.sourceNamed', { subject: this.subject })}
										@click=${() => {
											this.source = !this.source
										}}
										>${componentText('reader.view.source')}</schmancy-button
									>
									<schmancy-copy .value=${this.text} subject=${this.subject} size="xs"></schmancy-copy>
								`,
							)}
						</div>
					`,
				)}
				${when(
					empty,
					() =>
						html`<schmancy-typography type="body" token="sm" class="text-surface-on-variant"
							>${this.absent}</schmancy-typography
						>`,
					() => html`
						<div class="min-w-0">
							${when(
								this.presentation === 'document' && this.source,
								// The escape hatch exists to hand over exact bytes, so it wraps
								// them rather than cutting them at a hidden edge.
								() => html`
									<pre
										class="m-0 whitespace-pre-wrap wrap-anywhere rounded-xl bg-surface-containerLow p-4 font-mono text-sm leading-relaxed text-surface-on"
									>
${this.text}</pre>
								`,
								() => this.document(json),
							)}
						</div>
					`,
				)}
			</section>
		`
	}

	/** Prose keeps its reading measure; code can use the available width. */
	private document(json: string | null): TemplateResult {
		if (this.format === 'json' || json !== null) return html`${this.code(json ?? this.text, 'json')}<slot name="trailing"></slot>`
		const blocks = childrenOf(documentParser.parse(this.text).topNode)
		const last = blocks.at(-1)
		return html`
			<div data-document class="grid gap-4 min-w-0">
				${repeat(
					blocks,
					block => block.from,
					block => (block === last && block.name === 'Paragraph' ? this.paragraph(block, true) : this.block(block)),
				)}
				${last?.name === 'Paragraph' ? nothing : html`<slot name="trailing" style="--schmancy-trailing-offset: -0.5rem"></slot>`}
			</div>
		`
	}

	/**
	 * One block. The default arm is deliberate: a construct this does not name —
	 * a link-reference definition, a comment block — renders its own source as
	 * text rather than vanishing, so nothing the author sent is ever silently
	 * dropped from the reading face.
	 */
	private block(node: SyntaxNode): TemplateResult {
		if (node.name === 'Paragraph') return this.paragraph(node)

		if (node.name in HEADINGS) {
			const scale = HEADINGS[node.name]
			// A real h1-h6, so the document has an outline a reader can navigate by.
			// The scale rides inside it rather than replacing it.
			const inner = html`<schmancy-typography type=${scale.type} token=${scale.token}
				>${trimEnds(this.inline(node))}</schmancy-typography
			>`
			return HEADING_TAGS[scale.level](inner)
		}

		if (node.name === 'BulletList' || node.name === 'OrderedList') return this.list(node)

		if (node.name === 'Table') {
			const delimiter = node.getChild('TableDelimiter')
			const columns = (delimiter ? (this.slice(delimiter).match(/:?-+:?/g) ?? []) : []).map((marker, index) => ({
				index,
				align: marker.startsWith(':')
					? marker.endsWith(':')
						? 'center'
						: 'left'
					: marker.endsWith(':')
						? 'right'
						: 'start',
			}))
			const rows = childrenOf(node)
				.filter(row => row.name === 'TableHeader' || row.name === 'TableRow')
				.map(row => {
					const cells: (SyntaxNode | undefined)[] = []
					let column = 0
					let started = false
					// Empty cells have no TableCell node. The parser's delimiters keep
					// their columns in place; a leading pipe is outside the first cell.
					for (const child of childrenOf(row)) {
						if (child.name === 'TableCell') {
							cells[column] = child
							started = true
						} else if (started) column++
						else started = true
					}
					return { row, cells }
				})
			const header = rows.find(({ row }) => row.name === 'TableHeader')
			return html`
				<schmancy-scroll
					class="block min-w-0 max-w-prose"
					direction="horizontal"
					hide
					fade
					label=${this.subject || this.label}
				>
					<table class="border-collapse text-start" aria-label=${this.subject || this.label}>
						<thead>
							<tr>
								${repeat(
									columns,
									column => column.index,
									column => {
										const cell = header?.cells[column.index]
										return html`
											<th
												scope="col"
												class=${this.classMap({
													'pe-4 pb-1 align-bottom': true,
													'text-start': column.align === 'start',
													'text-left': column.align === 'left',
													'text-center': column.align === 'center',
													'text-right': column.align === 'right',
												})}
											>
												<schmancy-typography
													type="label"
													token="sm"
													class="min-w-24 max-w-xs wrap-anywhere text-surface-on-variant"
													>${cell === undefined ? '' : this.inline(cell)}</schmancy-typography
												>
											</th>
										`
									},
								)}
							</tr>
						</thead>
						<tbody>
							${repeat(
								rows.filter(({ row }) => row.name === 'TableRow'),
								({ row }) => row.from,
								({ cells }) => html`
									<tr>
										${repeat(
											columns,
											column => column.index,
											column => {
												const cell = cells[column.index]
												return html`
													<td
														class=${this.classMap({
															'pe-4 py-1 align-top': true,
															'text-start': column.align === 'start',
															'text-left': column.align === 'left',
															'text-center': column.align === 'center',
															'text-right': column.align === 'right',
														})}
													>
														<schmancy-typography type="body" token="sm" class="min-w-24 max-w-xs wrap-anywhere"
															>${cell === undefined ? '' : this.inline(cell)}</schmancy-typography
														>
													</td>
												`
											},
										)}
									</tr>
								`,
							)}
						</tbody>
					</table>
				</schmancy-scroll>
			`
		}

		if (node.name === 'Blockquote')
			return html`
				<blockquote class="m-0 grid gap-3 min-w-0 max-w-prose border-s-2 border-outline ps-4">
					${repeat(
						childrenOf(node).filter(child => !MARKS.has(child.name)),
						child => child.from,
						child => this.block(child),
					)}
				</blockquote>
			`

		if (node.name === 'FencedCode' || node.name === 'CodeBlock') {
			const diagram = this.diagrams.get(node.from)
			if (diagram?.image)
				return html`<schmancy-scroll
					class="block min-w-0"
					direction="horizontal"
					hide
					fade
					label=${diagram.alt || this.subject || this.label}
				>
					<img
						class="block max-w-none"
						src=${diagram.image}
						width=${ifDefined(diagram.width)}
						height=${ifDefined(diagram.height)}
						alt=${diagram.alt || this.subject || this.label}
					/>
				</schmancy-scroll>`
			const code = childrenOf(node).filter(child => child.name === 'CodeText')
			const source = code.length > 0 ? code.map(child => this.slice(child)).join('\n') : this.slice(node)
			const info = node.getChild('CodeInfo')
			const language = info ? this.slice(info).trim().split(/\s+/)[0].toLowerCase() : ''
			return html`
				${when(
					diagram?.error !== undefined,
					() =>
						html`<schmancy-typography type="body" token="sm" class="text-error-default"
							>${componentText('markdown.diagramFailed')}</schmancy-typography
						>`,
				)}
				${this.code(language === 'json' ? (formatJSON(source) ?? source) : source, language)}
			`
		}

		if (node.name === 'HorizontalRule') return html`<schmancy-divider></schmancy-divider>`

		// Raw HTML, and anything else with no mark of its own: the literal source,
		// in the face that says it is literal source. Never parsed as markup.
		return html`<schmancy-typography
			type="body"
			token="sm"
			class="max-w-prose whitespace-pre-wrap wrap-anywhere font-mono text-surface-on"
			>${this.slice(node)}</schmancy-typography
		>`
	}

	private code(source: string, language: string): TemplateResult {
		const fragments: Inline[] = []
		let position = 0
		if (Object.hasOwn(codeParsers, language)) {
			const tree = codeParsers[language].parse(source)
			const keys: SyntaxNode[] = []
			if (language === 'json')
				tree.iterate({
					enter(node) {
						if (node.name === 'Property' && node.node.firstChild?.name === 'String') keys.push(node.node.firstChild)
					},
				})
			let keyIndex = 0
			highlightTree(tree, language === 'json' ? jsonHighlighter : codeHighlighter, (from, to, classes) => {
				if (from > position) fragments.push(source.slice(position, from))
				while (keyIndex < keys.length && from >= keys[keyIndex].to) keyIndex++
				const key = keys[keyIndex]
				fragments.push(
					html`<span
						class=${this.classMap({
							[key && from >= key.from && to <= key.to ? 'text-primary-default' : classes]: true,
						})}
						>${source.slice(from, to)}</span
					>`,
				)
				position = to
			})
		}
		if (position < source.length) fragments.push(source.slice(position))
		return html`<schmancy-scroll
			class="block min-w-0 rounded-xl bg-surface-containerLow p-4"
			direction="horizontal"
			hide
			fade
			label=${this.subject || this.label}
		>
			<pre
				class=${this.classMap({
					'm-0 font-mono text-sm leading-relaxed text-surface-on': true,
					'whitespace-pre-wrap wrap-anywhere': language === 'json',
				})}
			><code>${fragments}</code></pre>
		</schmancy-scroll>`
	}

	/**
	 * A real list, so it reports as one and carries its own item count.
	 *
	 * The bullet and the number are the browser's ::marker rather than a text run
	 * of their own: a glyph rendered as content is an orphan run beside the item,
	 * announced as if it were part of the prose, and it takes the hanging indent
	 * with it. A list-style-type gives both for free.
	 */
	private list(node: SyntaxNode): TemplateResult {
		const items = childrenOf(node).filter(child => child.name === 'ListItem')
		const body = repeat(
			items,
			item => item.from,
			item => html`
				<li class="min-w-0">
					<div class="grid gap-2 min-w-0">
						${repeat(
							childrenOf(item).filter(child => !MARKS.has(child.name)),
							child => child.from,
							child => this.block(child),
						)}
					</div>
				</li>
			`,
		)
		const classes =
			'm-0 ps-5 grid gap-2 min-w-0 max-w-prose list-outside marker:text-surface-on-variant marker:tabular-nums'
		return node.name === 'OrderedList'
			? html`<ol class=${classes} style="list-style-type: decimal" start=${this.firstNumber(items)}>
					${body}
				</ol>`
			: html`<ul class=${classes} style="list-style-type: disc">
					${body}
				</ul>`
	}

	/** An ordered list starts where its author started it, not always at one. */
	private firstNumber(items: readonly SyntaxNode[]): number {
		const mark = items[0] ? childrenOf(items[0]).find(child => child.name === 'ListMark') : undefined
		const n = mark ? Number.parseInt(this.slice(mark), 10) : Number.NaN
		return Number.isFinite(n) ? n : 1
	}

	/**
	 * wrap-anywhere, NEVER break-words. Both break a long word only when it will
	 * not fit, but overflow-wrap:break-word leaves the box's MIN-CONTENT width at
	 * the length of that unbroken token, so the block outgrows its track under
	 * overflow-x:visible and the end of every line is unreachable with nothing on
	 * screen to say so. Measured at a 299px box on a prompt's own
	 * `[media part: data:image/jpeg;base64,…]` line — which is real in this
	 * corpus — the paragraph laid out 2035px wide, and a block of raw html 1559px.
	 * overflow-wrap:anywhere breaks the same words and ALSO shrinks min-content,
	 * so the box follows its track and prose still wraps at its spaces.
	 */
	private paragraph(node: SyntaxNode, closing = false): TemplateResult {
		// Read off THIS block's own source: a formatter's hard wrap is joined, and
		// every other newline is content and stays exactly where it was sent.
		const joined = isHardWrapped(this.slice(node))
		return html`<schmancy-typography
			dir="auto"
			type="body"
			token="md"
			class=${this.classMap({
				'max-w-prose wrap-anywhere': true,
				'whitespace-pre-wrap': !joined,
			})}
			>${this.inline(node, MARKS, joined)}${closing ? html`<slot name="trailing"></slot>` : nothing}</schmancy-typography
		>`
	}

	/**
	 * A block's own words, with each mark rendered in place. The gaps BETWEEN
	 * child nodes are the plain text, read straight out of the source, which is
	 * what keeps the newlines a paragraph carries.
	 */
	private inline(node: SyntaxNode, skip: ReadonlySet<string> = MARKS, join = false): readonly Inline[] {
		const parts: Inline[] = []
		let pos = node.from
		for (const child of childrenOf(node)) {
			if (child.from > pos) parts.push(this.soften(this.text.slice(pos, child.from), join))
			pos = child.to
			if (skip.has(child.name)) continue
			parts.push(this.mark(child, join))
		}
		if (node.to > pos) parts.push(this.soften(this.text.slice(pos, node.to), join))
		return parts
	}

	/** One inline mark, rendered as the mark it is. */
	private mark(node: SyntaxNode, join = false): Inline {
		if (node.name === 'InlineCode') {
			const parts = this.inline(node)
			let parent = node.parent
			while (parent !== null && parent.name !== 'TableCell') parent = parent.parent
			// GFM escapes a pipe even inside table code; ordinary code keeps its bytes.
			return html`<code class="font-mono text-primary-default"
				>${parent ? parts.map(part => (typeof part === 'string' ? part.replace(/\\\|/g, '|') : part)) : parts}</code
			>`
		}

		if (node.name === 'Emphasis') return html`<em>${this.inline(node)}</em>`

		if (node.name === 'StrongEmphasis') return html`<strong>${this.inline(node)}</strong>`

		if (node.name === 'Link' || node.name === 'Autolink') return this.link(node)

		if (node.name === 'Image') {
			const alt = this.inline(node, LINK_PARTS)
				.filter((part): part is string => typeof part === 'string')
				.join('')
				.trim()
			// A plain <em>, not <schmancy-typography>: this is a run INSIDE a
			// sentence, and typography is display:block — it would push the words
			// after the image onto a line of their own.
			return html`<em class="text-surface-on-variant"
				>${alt === '' ? componentText('markdown.imageUnnamed') : componentText('markdown.image', { alt })}</em
			>`
		}

		if (node.name === 'Escape') return this.text.slice(node.from + 1, node.to)

		if (node.name === 'Entity') return decodeEntity(this.slice(node))

		if (node.name === 'HardBreak') return html`<br />`

		// Raw HTML inline, a comment, an instruction: its own characters, as text.
		return this.soften(this.slice(node), join)
	}

	/** A link whose target this library is willing to hand a browser, or its text alone. */
	private link(node: SyntaxNode): Inline {
		const url = childrenOf(node).find(child => child.name === 'URL')
		// A `[shortcut]` or `[text][ref]` the document never defines is not a link
		// at all: it is the brackets its author typed, and a prompt line like
		// `[media part: data:image/jpeg;base64,…]` is one of them. Dropping those
		// brackets and painting the rest as a link edits the document twice over.
		if (!url) return this.soften(this.slice(node))

		const href = this.slice(url)
		// An autolink displays its own target — its words ARE the URL.
		const words = node.name === 'Autolink' ? [href] : trimEnds(this.inline(node, LINK_PARTS))
		if (words.length === 0) return this.soften(this.slice(node))
		const target = this.resolveHref ? this.resolveHref(href) : href
		if (target === null || !NAVIGABLE.test(target)) return html`<span class="text-primary-default">${words}</span>`
		return html`<schmancy-link href=${target} rel="noreferrer noopener">${words}</schmancy-link>`
	}

	private slice(node: SyntaxNode): string {
		return this.text.slice(node.from, node.to)
	}

	/** In a hard-wrapped paragraph, a soft line break is the space CommonMark makes of it. */
	private soften(text: string, join = false): string {
		return join ? text.replace(/\n[ \t]*/g, ' ') : text
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-markdown': SchmancyMarkdown
	}
}
